import {Box, Chip, Divider, ListItemIcon, MenuItem, MenuList, MenuProps, Typography} from '@mui/material';
import {AppInput, AppInputProps} from 'components/form';
import {Edit, Trash} from 'iconsax-react';
import {FC, ReactNode} from 'react';
import {AiOutlinePlus} from 'react-icons/ai';
import {AppPopover, AppPopoverProps} from './AppPopover';
import {AppIconButton} from 'components/buttons';

export interface AppDropDownMenuOptions {
    label: string | React.ReactNode;
    value: string;
    chip?: SelectChipView;
    isPinned?: boolean;
    isEditable?: boolean;
    isDeletable?: boolean;
}
export interface SelectChipView {
    color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
    label: string;
}

export interface AppDropDownMenuProps extends Omit<MenuProps, 'open'> {
    popoverProps?: Omit<AppPopoverProps, 'renderElement'>;
    showSearchField?: boolean;
    searchFieldTitle?: string;
    searchFieldProps?: AppInputProps;
    title?: string;
    paperWidth?: string;
    selectedItemValue?: string;
    addMenuItemTitle?: string;
    handleOptionSelect: (value: AppDropDownMenuOptions) => void;
    renderElement: (handleOpen: (event: any) => void, open: boolean) => React.ReactNode;
    handleOptionDelete?: (id: string) => void;
    options: AppDropDownMenuOptions[];
    handleOptionEdit?: (id: string) => void;
    onClickAddMenuItemHandler?: () => void;
    addMenuItemIcon?: ReactNode;
    renderOptions: (value: React.ReactNode) => React.ReactNode;
}

export const AppDropDownMenu: FC<AppDropDownMenuProps> = ({
    showSearchField = true,
    searchFieldTitle,
    onClickAddMenuItemHandler,
    addMenuItemTitle,
    renderElement,
    title,
    selectedItemValue,
    paperWidth = '250px',
    handleOptionSelect,
    renderOptions,
    handleOptionDelete,
    handleOptionEdit,
    options = [],
    popoverProps,
    addMenuItemIcon = <AiOutlinePlus style={{marginRight: '6px', color: 'black'}} />,
    ...rest
}) => {
    return (
        <>
            <AppPopover
                renderElement={renderElement}
                {...popoverProps}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                renderChildren={handleClose => {
                    return (
                        <Box width={paperWidth}>
                            {showSearchField && (
                                <AppInput
                                    style={{padding: '5px 10px 5px 10px'}}
                                    placeholder={searchFieldTitle}
                                    fullWidth
                                    autoComplete="off"
                                    {...rest.searchFieldProps}
                                />
                            )}
                            <Divider />
                            {options && options.length > 0 && (
                                <div
                                    style={{maxHeight: 'calc(100vh - 400px)', overflow: 'auto'}}
                                    id="spritnScrollableDiv"
                                >
                                    <MenuList disablePadding>
                                        {title && <MenuItem disabled>{title}</MenuItem>}
                                        {renderOptions(
                                            <Box key={'options-list'}>
                                                {options.map(option => (
                                                    <MenuItem
                                                        key={option.label + option.value}
                                                        sx={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                        }}
                                                        selected={selectedItemValue === option.value}
                                                        onClick={e => {
                                                            handleClose(e);
                                                            handleOptionSelect(option);
                                                        }}
                                                    >
                                                        <Box display={'flex'}>
                                                            <Typography
                                                                component={'span'}
                                                                variant="body2"
                                                                style={{whiteSpace: 'normal'}}
                                                            >
                                                                {option.label}
                                                            </Typography>
                                                        </Box>
                                                        <Box display={'flex'} alignItems="center">
                                                            {option.chip && (
                                                                <Chip
                                                                    sx={{
                                                                        ml: 1,
                                                                        fontSize: '9px',
                                                                        borderRadius: '8px',
                                                                    }}
                                                                    color={option.chip.color}
                                                                    size="small"
                                                                    variant="combined"
                                                                    label={option.chip.label}
                                                                />
                                                            )}
                                                            {handleOptionEdit &&
                                                                option.isEditable &&
                                                                option.value !== '' && (
                                                                    <AppIconButton
                                                                        size="small"
                                                                        onClick={e => {
                                                                            e.stopPropagation();
                                                                            handleOptionEdit(option.value);
                                                                        }}
                                                                    >
                                                                        <Edit variant={'Outline'} size={15} />
                                                                    </AppIconButton>
                                                                )}
                                                            {handleOptionDelete && option.isDeletable && (
                                                                <AppIconButton
                                                                    size="small"
                                                                    onClick={() => {
                                                                        handleOptionDelete(option.value);
                                                                    }}
                                                                >
                                                                    <Trash variant={'Bulk'} size={15} color="red" />
                                                                </AppIconButton>
                                                            )}
                                                        </Box>
                                                    </MenuItem>
                                                ))}
                                            </Box>,
                                        )}
                                    </MenuList>
                                </div>
                            )}
                            <Divider />
                            {addMenuItemTitle && (
                                <>
                                    <MenuItem
                                        onClick={e => {
                                            handleClose(e);
                                            onClickAddMenuItemHandler && onClickAddMenuItemHandler();
                                        }}
                                    >
                                        <ListItemIcon
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                paddingBottom: '10px',
                                            }}
                                        >
                                            {addMenuItemIcon}
                                            <span style={{color: 'black'}}>{addMenuItemTitle}</span>
                                        </ListItemIcon>
                                    </MenuItem>
                                </>
                            )}
                        </Box>
                    );
                }}
            ></AppPopover>
        </>
    );
};

export default AppDropDownMenu;
